import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { disableNetwork, enableNetwork } from "@firebase/firestore";
import { db } from "./firebase";
import { withTimeout } from "./async";
import { hasActiveVideoUploads } from "./video-upload";
import { diag, diagError } from "./diag";

/**
 * In the iOS/Android WebView, IndexedDB can be torn down while the app is in
 * the background ("Connection to Indexed Database server lost"), after which
 * Firestore stops working entirely — chats stop updating and screens spin
 * forever until the app is force-quit. Reload the page automatically instead
 * (at most once a minute, so a persistent problem can't cause a loop).
 */
const FATAL_PATTERNS = [
  /indexed ?database server lost/i,
  /FIRESTORE .*INTERNAL ASSERTION FAILED/i,
];
const RELOAD_KEY = "nn-fatal-reload-at";
const COUNT_KEY = "nn-fatal-reload-count";
const MIN_RELOAD_INTERVAL_MS = 60_000;
const MAX_RELOADS_PER_SESSION = 3;

function messageOf(reason: unknown): string {
  if (!reason) return "";
  if (typeof reason === "string") return reason;
  const err = reason as { message?: unknown };
  return typeof err.message === "string" ? err.message : String(reason);
}

function recover(reason: unknown) {
  const message = messageOf(reason);
  if (!FATAL_PATTERNS.some((pattern) => pattern.test(message))) return;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    const count = Number(sessionStorage.getItem(COUNT_KEY) || 0);
    if (Date.now() - last < MIN_RELOAD_INTERVAL_MS || count >= MAX_RELOADS_PER_SESSION) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(COUNT_KEY, String(count + 1));
  } catch {
    return;
  }
  console.error("Local database failed, reloading the app:", message);
  reloadApp("local database failed");
}

// A reload would kill a running video upload (it goes straight to Storage and
// doesn't need Firestore), so wait for it to finish first.
let reloadPending = false;
function reloadWhenIdle() {
  if (hasActiveVideoUploads()) {
    setTimeout(reloadWhenIdle, 3000);
    return;
  }
  window.location.reload();
}

/**
 * Restarts the app's web content, the same as closing and reopening it.
 * Returns false if it has to wait for a video upload to finish first.
 */
export function reloadApp(reason: string): boolean {
  diag("reload app", { reason, waitingForUpload: hasActiveVideoUploads() });
  const now = !hasActiveVideoUploads();
  if (!reloadPending) {
    reloadPending = true;
    reloadWhenIdle();
  }
  return now;
}

/** True while the user is typing somewhere (a reload would lose it). */
function isEditing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable;
}

// When the app last came to the foreground.
let lastVisibleAt = Date.now();
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") lastVisibleAt = Date.now();
  });
}

const AUTO_RELOAD_KEY = "nn-auto-reload-at";
const AUTO_RELOAD_COUNT_KEY = "nn-auto-reload-count";

/**
 * Reloads automatically when a screen has been stuck loading for a long
 * time, at most twice per session and never twice within two minutes, so a
 * real outage (no internet) can't cause a reload loop.
 */
export function autoReloadIfAllowed(reason: string): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  // Never restart while in the background or right after coming back.
  if (document.visibilityState !== "visible" || Date.now() - lastVisibleAt < 5_000) return false;
  // Nor while a video uploads or the user is typing.
  if (hasActiveVideoUploads() || isEditing()) return false;
  try {
    const last = Number(sessionStorage.getItem(AUTO_RELOAD_KEY) || 0);
    const count = Number(sessionStorage.getItem(AUTO_RELOAD_COUNT_KEY) || 0);
    if (Date.now() - last < 120_000 || count >= 2) return false;
    sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(AUTO_RELOAD_COUNT_KEY, String(count + 1));
  } catch {
    return false;
  }
  reloadApp(`auto: ${reason}`);
  return true;
}

export function installFirestoreRecovery() {
  window.addEventListener("unhandledrejection", (event) => recover(event.reason));
  window.addEventListener("error", (event) => recover(event.error ?? event.message));
  // Firestore reports some of these only through console.error.
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalError(...args);
    try {
      diagError(args.map((arg) => messageOf(arg)).join(" "));
    } catch {
      // Never let logging break error reporting.
    }
    for (const arg of args) recover(arg);
  };
  window.addEventListener("unhandledrejection", (event) =>
    diagError(`unhandled rejection: ${messageOf(event.reason)}`));
}

let reconnecting: Promise<void> | null = null;
let lastReconnectAt = 0;

/**
 * Drops and re-opens Firestore's connection. While the app is in the
 * background iOS kills its network connections, and Firestore then waits
 * up to a minute between reconnect attempts, so screens kept spinning after
 * the app was reopened. Cached data stays on screen during the reconnect and
 * queued writes are sent once it is back.
 */
export function reconnectFirestore(reason: string): Promise<void> {
  if (reconnecting) return reconnecting;
  if (Date.now() - lastReconnectAt < 5000) return Promise.resolve();
  lastReconnectAt = Date.now();
  diag("firestore reconnect", { reason });
  // Both calls are queued in order inside Firestore, so even if one is slow
  // the network always ends up enabled again.
  reconnecting = (async () => {
    try {
      await withTimeout(disableNetwork(db), 5000, "disable network");
    } catch (error) {
      console.warn("[Firestore] disableNetwork was slow", error);
    }
    try {
      await withTimeout(enableNetwork(db), 5000, "enable network");
    } catch (error) {
      console.warn("[Firestore] enableNetwork was slow", error);
    }
  })().finally(() => {
    reconnecting = null;
  });
  return reconnecting;
}

const RECONNECT_AFTER_BACKGROUND_MS = 15_000;

export function installFirestoreReconnect() {
  let backgroundSince = 0;
  const onHidden = () => {
    backgroundSince = Date.now();
  };
  const onVisible = (source: string) => {
    if (backgroundSince && Date.now() - backgroundSince > RECONNECT_AFTER_BACKGROUND_MS) {
      void reconnectFirestore(source);
    }
    backgroundSince = 0;
  };
  if (Capacitor.isNativePlatform()) {
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) onVisible("app resumed");
      else onHidden();
    }).catch((error) => console.warn("[Firestore] Could not watch app state", error));
  } else {
    // Browsers throttle background tabs the same way.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onVisible("tab visible again");
      else onHidden();
    });
  }
  window.addEventListener("online", () => void reconnectFirestore("back online"));
}
